import {FC, createContext, useContext, useState, useEffect, ReactNode} from 'react'
import {IntlProvider} from 'react-intl'

import enMessages from './messages/en.json'
import svMessages from './messages/sv.json'
import finMessages from './messages/fin.json'
import noMessages from './messages/no.json'

const I18N_CONFIG_KEY = process.env.REACT_APP_I18N_CONFIG_KEY || 'i18nConfig'

export type Lang = 'en' | 'sv' | 'fin' | 'no'

type I18nContextProps = {
  selectedLang: Lang
  setSelectedLang: (lang: Lang) => void
}

const initialState: I18nContextProps = {
  selectedLang: 'en',
  setSelectedLang: () => {},
}

const I18nContext = createContext<I18nContextProps>(initialState)

const getInitialLang = (): Lang => {
  try {
    const ls = localStorage.getItem(I18N_CONFIG_KEY)
    if (ls) {
      return JSON.parse(ls).selectedLang as Lang
    }
  } catch (err) {
    console.error('Error reading lang from localStorage', err)
  }
  return 'en'
}

const messages = {
  en: enMessages,
  sv: svMessages,
  fin: finMessages,
  no: noMessages,
}

type Props = {
  children: ReactNode
}

export const MetronicI18nProvider: FC<Props> = ({children}) => {
  const [selectedLang, setSelectedLangState] = useState<Lang>(getInitialLang())

  const setSelectedLang = (lang: Lang) => {
    setSelectedLangState(lang)
    localStorage.setItem(I18N_CONFIG_KEY, JSON.stringify({selectedLang: lang}))
  }

  useEffect(() => {
    // sync with i18n library if needed
  }, [selectedLang])

  return (
    <I18nContext.Provider value={{selectedLang, setSelectedLang}}>
      <IntlProvider locale={selectedLang} messages={messages[selectedLang]}>
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  )
}

export const useLang = () => useContext(I18nContext).selectedLang
export const useSetLang = () => useContext(I18nContext).setSelectedLang
